import type { PropostaItem } from '@/types'
import { extractServices } from '@/utils/dealCalculations'
import {
  buildInvestmentLabelFromItem,
  buildLoyaltyLabelFromItem,
  buildPaymentDetailsFromItem,
} from '@/utils/proposalExport'

export type PublicScopeService = {
  name: string
  quantity?: string
  isPartial?: boolean
}

export type PublicScopeCategory = {
  category: string
  services: PublicScopeService[]
}

export type PublicPlanView = {
  item: PropostaItem
  title: string
  subtitle?: string
  tipoLabel: string
  tipoColor: string
  investmentLabel: string
  paymentDetails: string
  loyaltyLabel: string
  bonus?: string
  categories: PublicScopeCategory[]
  scopeCount: number
}

/** Mensalidade do item. O total do contrato (12x) não entra no resumo público. */
export function publicMonthlyAmount(item: PropostaItem): number {
  const pricing = item.pricing
  const isRecorrente = pricing?.recorrente ?? (item.tipo === 'plano' || item.tipo === 'servico')
  const total = Number(pricing?.total ?? item.total ?? 0)
  const installments = Number(pricing?.installments || item.duration || (isRecorrente ? 12 : 1)) || 1
  const monthly = Number(pricing?.installmentValue ?? 0)

  if (isRecorrente) {
    if (monthly > 0) return monthly
    if (installments > 1 && total > 0) return total / installments
    return Number(item.valorUnitario ?? total)
  }

  if (monthly > 0 && installments > 1) return monthly
  return Number(item.valorUnitario ?? total)
}

const TIPO_META: Record<string, { label: string; color: string }> = {
  plano: { label: 'Plano Recorrente', color: '#d4145a' },
  servico: { label: 'Serviço', color: '#2563EB' },
  avulso: { label: 'Projeto', color: '#E35933' },
}

export function buildPublicPlanViews(items: PropostaItem[]): PublicPlanView[] {
  return items.map((item) => {
    const rawCategories = extractServices(item)
    const categories: PublicScopeCategory[] = rawCategories
      .map((cat) => ({
        category: String(cat.category || cat.categoria || 'Escopo'),
        services: (cat.services || [])
          .filter((svc: { isIncluded?: boolean; name?: string; nome?: string; quantity?: string | number; quantidade?: string | number; isPartial?: boolean }) => svc.isIncluded !== false)
          .map((svc: { isIncluded?: boolean; name?: string; nome?: string; quantity?: string | number; quantidade?: string | number; isPartial?: boolean }) => ({
            name: String(svc.name || svc.nome || '').trim(),
            quantity: svc.quantity || svc.quantidade,
            isPartial: Boolean(svc.isPartial),
          }))
          .filter((svc: { name: string }) => svc.name.length > 0),
      }))
      .filter((cat) => cat.services.length > 0)

    const meta = TIPO_META[item.tipo] ?? TIPO_META.avulso
    const scopeCount = categories.reduce((sum, cat) => sum + cat.services.length, 0)

    return {
      item,
      title: item.descricao || item.nome || item.name || 'Plano',
      subtitle: item.description,
      tipoLabel: meta.label,
      tipoColor: meta.color,
      investmentLabel: buildInvestmentLabelFromItem(item),
      paymentDetails: buildPaymentDetailsFromItem(item),
      loyaltyLabel: buildLoyaltyLabelFromItem(item),
      bonus: item.showBonus === true && item.bonus ? item.bonus : undefined,
      categories,
      scopeCount,
    }
  })
}
